// src/utils/validators/validators.ts
import * as Yup from 'yup';


// Email validator ------------------------------------------------
export const emailValidator = Yup.string()
  .email('Please enter a valid email address')
  .max(254, 'Email address must be at most 254 characters')
  .matches(
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
    'Please enter a valid email address'
  )
  .required('Email is required');

// Password validator ------------------------------------------
export const passwordValidator = Yup.string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be at most 128 characters')
  .matches(
    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
    'Password must contain at least one letter, one number, one lowercase letter, and one uppercase letter'
  )
  .required('Password is required');

// Activation code validator -------------------------------------
export const activationCodeValidator = Yup.string()
  .matches(/^\d{5}$/, 'Activation code must be a 5-digit number')
  .required('Activation code is required');

// Boolean validator ---------------------------------------------
export const booleanValidator = (message = 'This field must be accepted') =>
  Yup.boolean()
    .oneOf([true], message)
    .required(message);

// Name validator ------------------------------------------------
export const nameValidator = Yup.string()
  .max(30, "Name must be at most 30 characters")
  .nullable();

// Family validator ----------------------------------------------
export const familyValidator = Yup.string()
  .max(30, "Family name must be at most 30 characters")
  .nullable();

// Mobile number validator ----------------------------------------
export const mobileNumberValidator = Yup.string()
  .matches(
    /^\+[1-9]\d{9,14}$/,  // E.164: + followed by 10 to 15 digits (first digit not 0)
    "Enter a valid international mobile number starting with + (e.g., +12345678901)."
  )
  .required("Mobile number is required.");

// Birthday validator --------------------------------------------
export const birthdayValidator = Yup.date()
  .nullable()
  .max(new Date(), "Birthday cannot be in the future");

// Gender validator ----------------------------------------------
// export const genderValidator = Yup.string()
//   .oneOf(["Male", "Female", ""], "Invalid gender")
//   .nullable();

// Validator for 4-digit PIN -------------------------------------
export const pinValidator = Yup.string()
  .length(4, "PIN must be exactly 4 digits.")
  .matches(/^\d{4}$/, "PIN must be numeric.")

// Validator for 4-digit Sequrity PIN -----------------------------
export const sequrityPinValidator = Yup.string()
  .matches(/^\d{4}$/, "PIN must be exactly 4 digits and numeric.")
  .length(4, "PIN must be exactly 4 digits.")
  .required("PIN is required.");

// RSA Password Validator -----------------------------------------
export const rsaPasswordValidator = Yup.string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be at most 128 characters')
  .matches(
    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
    'Password must contain at least one letter, one number, one lowercase letter, and one uppercase letter'
  )
  .required('Password is required for RSA encryption');

// OTP Cpde Validator ---------------------------------------------
export const otpValidator = Yup.string()
  .length(6, 'OTP must be exactly 6 digits.')
  .matches(/^\d{6}$/, 'OTP must be numeric.')
  .required('OTP is required.');

// Amount Validator -----------------------------------------------
export const amountValidator = Yup.number()
  .typeError("Amount must be a number.")
  .positive("Amount must be greater than zero.")
  .max(10000, "Maximum allowed donation is $10,000")
  .required("Donation amount is required.");

// Message Validator -----------------------------------------------
export const messageValidator = Yup.string()
  .max(500, "Message must be under 500 characters.")
  .nullable();


  
// --------------------------------------------------------------------------------------------------------------------------
// Validators for Academic Record fields
export const academicRecordValidators = {
  education_document_type: Yup.string().required("Document type is required."),
  education_degree: Yup.string().required("Degree type is required."),
  school: Yup.string().required("School is required."),
  country: Yup.string().required("Country is required."),
  graduation_year: Yup.number()
    .nullable()
    .min(1950, "Year must be after 1950")
    .max(new Date().getFullYear(), "Year cannot be in the future."),
  is_theology_related: Yup.boolean(),
};

export const educationDocumentTypeValidator = (documentTypes: [string, string][]) =>
  Yup.string()
    .oneOf(
      documentTypes?.map(([value]) => value),
      "Invalid document type selected."
    )
    .required("Document type is required.");

export const educationDegreeValidator = (degrees: [string, string][]) =>
  Yup.string()
    .oneOf(
      degrees?.map(([value]) => value),
      "Invalid degree selected."
    )
    .required("Degree type is required.");


export const schoolValidator = Yup.string()
  .trim()
  .matches(/^[a-zA-Z0-9\s\-.,'"]+$/, "Invalid characters in school name.")
  .min(2, "School name must be at least 2 characters.")
  .max(100, "School name must be less than 100 characters.")
  .required("School is required.");

export const countryValidator = (countries: { value: string; label: string }[]) =>
  Yup.string()
    .oneOf(
      countries.map((option) => option.value),
      "Invalid country selected."
    );
// .required("Country is required.");
// --------------------------------------------------------------------------------------------------------------------------





export const genderValidator = (genders: { value: string; label: string }[]) =>
  Yup.string()
    .oneOf(
      genders.map((option) => option.value),
      "Invalid gender selected."
    );



export const graduationYearValidator = Yup.number()
  .nullable()
  .min(1900, "Graduation year must be between 1900 and the current year.")
  .max(new Date().getFullYear(), `Graduation year cannot be after ${new Date().getFullYear()}.`);

export const isTeologyRelatedValidator = Yup.boolean();






// Validator for Social Media URLs ------------------------------------------------------------
export const validateUrl = (url: string): string | null => {
  if (!url) {
    return "URL is required.";
  }

  // Simple URL regex validation
  const urlPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;
  if (!urlPattern.test(url)) {
    return "Please enter a valid URL.";
  }

  return null; // No errors
};


// Validator for Gift Survay -----------------------------------------------------------------
export const giftLanguageSchema = Yup.object().shape({
  gift_language: Yup.string()
    .required("Please select a language to begin the survey."),
});


// -------------------------------------------------------------------
// General text field validator (with customizable max)
export const textFieldValidator = (fieldName = "Text", max = 1000) =>
  Yup.string()
    .max(max, `${fieldName} must be at most ${max} characters.`)
    .nullable();


export const pastDateValidator = (fieldName = "Date") =>
  Yup.date()
    .nullable()
    .max(new Date(), `${fieldName} cannot be in the future.`);


export const selectRequiredValidator = (fieldName = "Selection") =>
  Yup.string().required(`${fieldName} is required.`);
